import React, { useMemo, useEffect } from "react";
import { useCommonStateFields } from "features/queryReports/hooks/useCommonStateFields";
import { applyCommonOutputs, applyGeographyParams } from "features/queryReports/utils/queryCommon";
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import cbsa from 'util/cbsa.js';
import states from 'util/states.js';
import { underZipLimit, validateZipCodes } from 'util/zip.js';
import { readableRefDate } from 'util/medicareRefDate.js';
import classes from './Reports.module.scss';
import { disableSubmitButton, formatDate, getOneYearsBack, validateFileName } from 'features/queryReports/utils/commonFunctions';
import QueryCreatedModal from 'features/queryReports/components/QueryCreatedModal';
import { useSubmitQuery } from 'features/queryReports/hooks/useSubmitQuery';
import { marketShareIDs } from 'features/queryReports/utils/reportIds';
import {
  CBSAInput,
  DateRange,
  CheckboxInput,
  FileNameInput,
  GeographyTypeSelect,
  PatientZip3Input,
  ProviderTypeSelect,
  RefDateSelect,
  StateSelect,
  VisualizationToggle,
  ZipCodeInput
} from 'features/queryReports/components/FormFields';
import marketShareOutputFields from "features/queryReports/utils/reportOutputFields/MarketShareOutputFields";

function QueryLogicDescription({
  payerMix,
  providerType,
  startDate,
  endDate,
  medicareRefDate,
  queryObj
}) {
  return (
    <span>
      I would like to see <strong>{providerType ? providerType + 's' : ''}</strong> located in the{' '}
      {queryObj.parameters.geography_type === 'zipcode' &&
        <strong>
          ZIP Code{queryObj.parameters.zips.length > 1 ? 's' : ''} of{' '}
          {queryObj.parameters.zips.length > 1
            ? queryObj.parameters.zips.join(', ')
            : queryObj.parameters.zips[0] || ''}
        </strong>
      }
      {queryObj.parameters.geography_type === 'cbsa' &&
        <strong>
          CBSA of {queryObj.parameters.cbsa ? cbsa.find(({ code }) => code === queryObj.parameters.cbsa).name : ''}
        </strong>
      }
      {queryObj.parameters.geography_type === 'state' &&
        <strong>
          State of{' '}
          {queryObj.parameters.state ? states.find(({ value }) => value === queryObj.parameters.state).name : ''}
        </strong>
      }
      {queryObj.parameters.geography_type === 'patientZip3' &&
        <strong>
          ZIP3 code{queryObj.parameters.patientZip3s.length > 1 ? 's' : ''} of{' '}
          {queryObj.parameters.patientZip3s.length > 1
            ? queryObj.parameters.patientZip3s.join(', ')
            : queryObj.parameters.patientZip3s[0] || ''}
        </strong>
      }
      {' '}
      who saw {payerMix === 'commercial-and-medicare' && <strong>Medicare and Commercial</strong>}
      {payerMix !== 'commercial-and-medicare' && <strong>{payerMix}</strong>} patients{' '}
      {payerMix === 'commercial' && (
        <span>
          between <strong>{startDate?.toLocaleDateString()}</strong> and{' '}
          <strong>{endDate?.toLocaleDateString()}</strong>
        </span>
      )}
      {(payerMix === 'medicare' || payerMix === 'commercial-and-medicare') && (
        <span>
          between <strong>{readableRefDate(medicareRefDate)}</strong>
        </span>
      )}
      .
    </span>
  );
}

function MarketShare({ bridge, payerMixValue, initialParams, showOpenMyQueries }) {
  const {
    categoryId,
    commercialOrgReportId,
    commercialPhysReportId,
    medicareOrgReportId,
    medicarePhysReportId,
    allpayerOrgReportId,
    allpayerPhysReportId
  } = marketShareIDs;
  const {
    state: common,
    bind,
    set,
    setOutput,
    resetCommon,
    hydrateFrom,
    selectors,
  } = useCommonStateFields({
    overrides: { payerMix: payerMixValue ?? "" },
  });

  const {
    submitQuery,
    waiting,
    showModal,
    modalData,
    closeModal,
  } = useSubmitQuery({ bridge, categoryId, classes, fileName: common.fileName });

  const confirmQuerySuccess = type => {
    if (type === 1) {
      closeModal();
      bridge.onMyQueryOpen();
    } else if (type === 2) {
      closeModal();
      bridge.onQuerySubmitted();
    }
  };

  const applyIncomingParams = data => {
    if (data.reportId === commercialPhysReportId ||
      data.reportId === commercialOrgReportId) {
      set("payerMix", "commercial");
    } else if (
      data.reportId === medicarePhysReportId ||
      data.reportId === medicareOrgReportId
    ) {
      set("payerMix", "medicare");
    } else if (
      data.reportId === allpayerPhysReportId ||
      data.reportId === allpayerOrgReportId
    ) {
      set("payerMix", "commercial-and-medicare");
    }

    if (
      data.reportId === commercialOrgReportId ||
      data.reportId === medicareOrgReportId ||
      data.reportId === allpayerOrgReportId
    ) {
      set("providerType", "organization");
    } else if (
      data.reportId === commercialPhysReportId ||
      data.reportId === medicarePhysReportId ||
      data.reportId === allpayerPhysReportId
    ) {
      set("providerType", "physician");
    }
  }

  useEffect(() => {
    if (initialParams) {
      hydrateFrom(initialParams);
      applyIncomingParams(initialParams); // local compute set at initial data
      // any page-only hydration here as needed
    } else if (bridge?.getPayerMix) {
      const mix = bridge.getPayerMix();
      if (mix) {
        set('payerMix', mix);
        if (mix === 'commercial-and-medicare') set('canBeVisualized', false);
      }
    }
  }, [initialParams]);

  const queryObj = useMemo(() => {
    const retObj = {
      description: common.fileName,
      parameters: {
        geography_type: common.geographyType,
        canBeVisualized: common.canBeVisualized,
      },
      outputFields: [],
    };

    // common outputs
    applyCommonOutputs(retObj, common.outputs, {
      allowList: [
        'address',
        'city',
        'stateName',
        'zipCode',
        'distinctClaimsCount',
        'distinctPatientsCount',
        'sumCharges'
      ]
    });

    if (selectors.isCommercial) {
      if (common.outputs.year) retObj.outputFields.push("Year");
      if (common.outputs.month) retObj.outputFields.push("Month");
    }

    if (selectors.isAllPayer && common.outputs.source) {
      retObj.outputFields.push("Source");
    }

    if (selectors.isCommercial) {
      retObj.parameters.start_date = formatDate(common.startDate);
      retObj.parameters.end_date = formatDate(common.endDate);
    } else if (selectors.isMedicare) {
      retObj.parameters.medicare_ref_date = common.medicareRefDate;
    } else if (selectors.isAllPayer) {
      retObj.parameters.quarter = common.medicareRefDate;
      if (common.outputs.proceduresCount) { retObj.outputFields.push('Count of Procedures'); }
    }

    if (common.providerType === "physician") {
      if (selectors.isCommercial) {
        retObj.reportId = commercialPhysReportId;
      } else if (selectors.isMedicare) {
        retObj.reportId = medicarePhysReportId;
      } else if (selectors.isAllPayer) {
        retObj.reportId = allpayerPhysReportId;
      }

      // Provider outputs using common toggles
      if (common.outputs.npi) retObj.outputFields.push("Physician NPI");
      if (common.outputs.name) retObj.outputFields.push("Physician Name");
      if (common.outputs.classification) retObj.outputFields.push("Physician Classification");
      if (common.outputs.specialization) retObj.outputFields.push("Physician Specialization");
      if (common.outputs.hospitalAffiliationIds) retObj.outputFields.push("Hospital Affiliation IDs");
      if (common.outputs.hospitalAffiliationNames) retObj.outputFields.push("Hospital Affiliation Names");
      if (common.outputs.groupPracticeIds) retObj.outputFields.push("Group Practice IDs");
      if (common.outputs.groupPracticeNames) retObj.outputFields.push("Group Practice Names");
      // Page-only outputs (keep from your local state)
    } else if (common.providerType === 'organization') {
      if (selectors.isCommercial) {
        retObj.reportId = commercialOrgReportId;
      } else if (selectors.isMedicare) {
        retObj.reportId = medicareOrgReportId;
      } else if (selectors.isAllPayer) {
        retObj.reportId = allpayerOrgReportId;
      }
      if (common.outputs.npi) retObj.outputFields.push("Organization NPI");
      if (common.outputs.name) retObj.outputFields.push("Organization Name");
      if (common.outputs.classification) retObj.outputFields.push("Organization Classification");
      if (common.outputs.specialization) retObj.outputFields.push("Organization Specialization");
    }

    applyGeographyParams(retObj, {
      geographyType: common.geographyType,
      zipCodes: common.zipCodes,
      cbsaSelection: common.cbsaSelection,
      stateSelection: common.stateSelection,
      patientZip3s: common.patientZip3s,
    });

    if (common.canBeVisualized) { //all fields active if visualization checkbox checked
      retObj.outputFields = [
        'Address',
        'City',
        'ZIP Code',
        'Count of Distinct Claims',
        'Count of Distinct Patients',
        'Sum of Charges'
      ];
      if (selectors.isCommercial) {
        retObj.outputFields = retObj.outputFields.concat(['Year', 'Month']);
      }
      if (common.providerType === 'physician') {
        retObj.outputFields = retObj.outputFields.concat([
          'Physician NPI',
          'Physician Name',
          'Physician Classification',
          'Physician Specialization',
          'Hospital Affiliation IDs',
          'Hospital Affiliation Names',
          'Group Practice IDs',
          'Group Practice Names'
        ]);
      } else if (common.providerType === 'organization') {
        retObj.outputFields = retObj.outputFields.concat([
          'Organization NPI',
          'Organization Name',
          'Organization Classification',
          'Organization Specialization'
        ]);
      }
      if (selectors.isAllPayer) {
        retObj.outputFields = retObj.outputFields.concat([
          'Count of Procedures',
          'State',
          'Source'
        ]);
      }
    }

    return retObj;
  }, [
    common.fileName,
    common.providerType,
    common.geographyType,
    common.zipCodes,
    common.cbsaSelection,
    common.stateSelection,
    common.patientZip3s,
    common.startDate,
    common.endDate,
    common.medicareRefDate,
    common.canBeVisualized,
    selectors.isCommercial,
    selectors.isMedicare,
    selectors.isAllPayer,
    common.outputs
  ]);

  function clearFields() {
    if (!showOpenMyQueries) {
      bridge?.onClickCancelQueryReport();
      return;
    }
    resetCommon(); // clears shared fields
    if (selectors.isCommercial) {
      set('patientZip3s', '');
      set('startDate', getOneYearsBack());
      set('endDate', new Date());
    }

    if (selectors.isMedicare || selectors.isAllPayer) {
      set('medicareRefDate', null);
    }
    // clear page-only here
  }

  return (
    <div className="container-fluid p-3">
      <div className={classes['report-content-wrap']}>
        <FileNameInput
          fileName={common.fileName}
          setFileName={(v) => set("fileName", v)}
          priorQueryParams={common.priorQueryParams}
        />
        <ProviderTypeSelect bind={bind} label="Provider Type:" overlayText="Group claim counts by either physician or organizations" />

        {selectors.isMedicare && (
          <RefDateSelect
            id="medicarerefdate"
            label="Medicare Reference Date:"
            {...bind.select("medicareRefDate")}
            placeholder="Select Medicare Reference Date"
            overlayText="Medicare reference date"
          />
        )}

        {selectors.isAllPayer && (
          <RefDateSelect
            id="medicarerefdate"
            label="Commercial + Medicare Reference Date:"
            {...bind.select("medicareRefDate")}
            placeholder="Select Commercial + Medicare Reference Date"
            overlayText="Commercial + Medicare reference date"
          />
        )}

        <GeographyTypeSelect
          bind={bind}
          common={common}
          allowedOptions={{
            zipcode: true,
            cbsa: true,
            state: true,
            patientZip3: common.payerMix === "commercial"
          }}
        />

        {common.geographyType === 'zipcode' && (
          <ZipCodeInput bind={bind} common={common} />
        )}

        {common.geographyType === 'cbsa' && (
          <CBSAInput set={set} common={common} />
        )}

        {common.geographyType === 'state' && (
          <StateSelect set={set} common={common} />
        )}

        {common.geographyType === 'patientZip3' && (
          <PatientZip3Input common={common} bind={bind} />
        )}
        {selectors.isCommercial && (
          <DateRange common={common} bind={bind} label="Date Range" overlayText="Dates you want to use to measure" />
        )}
        <hr />
        {common.payerMix !== 'commercial-and-medicare' && (
          <VisualizationToggle common={common} bind={bind} />
        )}

        {common.providerType && common.geographyType && (
          <div className="row mb-3">
            <div className={classes['querylogic-wrapper']}>
              <FontAwesomeIcon icon={faCircleInfo} className={classes['info-icon']} />
              <div>
                <div className={classes['header']}>Query Logic</div>
                <div className={classes['body']}>
                  <QueryLogicDescription
                    payerMix={common.payerMix}
                    providerType={common.providerType}
                    startDate={common.startDate}
                    endDate={common.endDate}
                    medicareRefDate={common.medicareRefDate}
                    queryObj={queryObj}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {common.providerType && (
          <div className="row mb-3">
            <div className={classes["querylogic-wrapper"]}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={classes["info-icon"]}
              />
              <div>
                <div className={classes["header"]}>Output Fields</div>
                <div className={classes["body"]}>
                  {marketShareOutputFields.map((item, index) => (
                    <CheckboxInput
                      key={item.name || index}
                      item={item}
                      common={common}
                      selectors={selectors}
                      setOutput={setOutput}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {common.fileName &&
          common.providerType &&
          common.startDate &&
          common.endDate &&
          (common.medicareRefDate || selectors.isCommercial) &&
          ((common.geographyType === 'zipcode' &&
            common.zipCodes &&
            validateZipCodes(common.zipCodes, ['zip', 'zip+4']) &&
            underZipLimit(common.zipCodes, 500)) ||
            (common.geographyType === 'cbsa' && common.cbsaSelection) ||
            (common.geographyType === 'state' && common.stateSelection) ||
            (common.geographyType === 'patientZip3' &&
              common.patientZip3s &&
              validateZipCodes(common.patientZip3s, ['zip3']) &&
              underZipLimit(common.patientZip3s, 20))) && (
            <div className={`row ${classes['submit-btn-wrap']}`}>
              <button
                type="button"
                className={`btn btn-primary mr-2 ${classes['submit-btn']}`}
                disabled={!validateFileName(common.fileName) || disableSubmitButton(common)}
                onClick={() => submitQuery(queryObj)}
              >
                Submit Query
                {waiting && (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                )}
              </button>
              <button
                type="button"
                className={`btn btn-secondary ${classes['cancel-btn']}`}
                onClick={() => clearFields()}
              >
                Cancel
              </button>
            </div>
          )}

        <QueryCreatedModal
          show={showModal}
          header={modalData.header}
          body={modalData.body}
          classes={classes}
          onClose={() => confirmQuerySuccess(2)}
          onOpenMyQueries={() => confirmQuerySuccess(1)}
          showOpenMyQueries={showOpenMyQueries}
        />

      </div>
    </div>
  );
}

export default MarketShare;